import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';

import { FieldDefinition, SortInfo } from '@stylehaven/components';
import { ApiFragment } from '../record/entities/api-fragment.entity';
import { PaginatedResults } from '../record/entities/pagination/paginated-results.entity';
import { PaginationInfo } from '../record/entities/pagination/pagination-info.entity';
import { PaginationPreferences } from '../record/entities/pagination/pagination-preferences.entity';
import { RecordSearch } from '../record/entities/record-search.entity';
import { OverviewOptionsService } from '../services/store/overview-options.service';
import { OverviewOptions } from '../state/overview/overview-options.entity';
import { Params } from '@angular/router';

@Injectable()
export abstract class GenericOverviewContainerSandboxService<T> {
    protected constructor(protected viewOptionsService: OverviewOptionsService) {}

    public setPaginationInfo(viewId: string, paginationInfo: PaginationInfo): void {
        this.viewOptionsService.setPaginationPreferences(viewId, {
            page: paginationInfo.page,
            size: paginationInfo.resultsPerPage
        });
    }

    public setPaginationPreferences(viewId: string, paginationPreferences: PaginationPreferences): void {
        this.viewOptionsService.setPaginationPreferences(viewId, paginationPreferences);
    }

    /** `undefined` clears the stored sort, which is how a view falls back to the page's default order. */
    public setSort(view: string, sort: SortInfo | undefined): void {
        this.viewOptionsService.setSort(view, sort);
    }

    public initializePageCache(view: string, index: number, page: PaginatedResults<T> | null): void {
        if (page === null) {
            return;
        }

        // Explicit cast to Page of ApiFragment, tasks are not currently supported.
        this.viewOptionsService.initializePageCache(view, index, page as unknown as PaginatedResults<ApiFragment>);
    }

    public setQueryParams(view: string, queryParams?: Params): void {
        this.viewOptionsService.setQueryParams(view, queryParams);
    }

    public setRecordSearch(view: string, recordSearch: RecordSearch): void {
        this.viewOptionsService.setRecordSearch(view, recordSearch);
    }

    public getOverviewOptions(view: string): Observable<OverviewOptions | undefined> {
        return this.viewOptionsService.getOverviewOptions(view);
    }

    public pushToStack(view: string, topLevelOverview: boolean): void {
        this.viewOptionsService.clearStackUpTo(view);
        this.viewOptionsService.pushToStack(view, topLevelOverview);
    }

    /**
     * For an overview the router kept alive across a route change ("Vorig/Volgend resultaat"): the entry it
     * pushed now stands for the new route, so it is swapped out rather than left behind for the header's
     * "Terug naar" fallback to land on.
     */
    public replaceOnStack(previous: string, view: string, topLevelOverview: boolean): void {
        this.viewOptionsService.clearStackUpTo(previous);
        this.pushToStack(view, topLevelOverview);
    }

    public setBackToSearchLabel(overviewId: string, backToSearchLabel: string): void {
        this.viewOptionsService.setBackToSearchLabel(overviewId, backToSearchLabel);
    }

    public setExpandedIds(view: string, expandedIds: string[]): void {
        this.viewOptionsService.setExpandedIds(view, expandedIds);
    }

    abstract getById(id: string): Observable<T>;

    abstract search(recordSearch: RecordSearch): Observable<PaginatedResults<T>>;

    abstract getFieldDefinitions(): Observable<FieldDefinition[]>;
}
